/**
 * TaskTimelineEditor — the project-detail row's "expanded state": a draggable
 * timeline for a task's work window (started_at → completed_at). Drag the Start
 * or Finish handle to adjust how long the task took; the duration updates live.
 * Datetime inputs sit beneath the bar for precise / gross corrections (a forgotten
 * Start that inflated the clock — typing the real time beats dragging across a
 * multi-hour window).
 *
 * Supervisor/admin only; persists via PATCH /tasks/:id (started_at/completed_at),
 * the same endpoint behind the task-detail "Adjust times".
 *
 * Today the crew flow is single-stage (Start → Finish; en-route/on-site/pause are
 * retired), so the work window is the whole timeline — one "working" segment. If
 * pause/resume stages return, they'd render as extra segments with their own
 * draggable boundaries between these two ends.
 */
import { useEffect, useReducer, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { format, parseISO } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUpdateTask } from '@/api/tasks';
import { Button } from '@/components/ui/Button';
import { countedWorkedMinutes, localScheduledDays } from '@/lib/laborDuration';

const SNAP_MS = 5 * 60_000; // drag snaps to 5-minute steps
const MIN_PAD = 30 * 60_000;

/** The visible time axis: the work window plus padding so handles can drag out. */
function computeWindow(a: number, b: number): { lo: number; hi: number } {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const pad = Math.max(MIN_PAD, (hi - lo) * 0.25);
  return { lo: lo - pad, hi: hi + pad };
}

function toLocalInput(ms: number): string {
  return format(new Date(ms), "yyyy-MM-dd'T'HH:mm");
}
function localToMs(v: string): number {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? NaN : d.getTime();
}
function fmtDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

const TWO_HOURS_MS = 2 * 60 * 60_000;

/** First of the ISO candidates that parses to a finite instant, else null. */
function firstMs(...candidates: Array<string | null | undefined>): number | null {
  for (const c of candidates) {
    if (!c) continue;
    const ms = parseISO(c).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

export function TaskTimelineEditor({
  taskId,
  startedAt,
  completedAt,
  scheduledStart,
  scheduledEnd,
  onClose,
}: {
  taskId: string;
  startedAt: string | null;
  completedAt: string | null;
  /** Fallback anchors when a task was completed without a recorded timer. */
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  onClose: () => void;
}) {
  const update = useUpdateTask(taskId);
  // Seed the sliders. A task completed without ever being Started (started_at
  // null) still gets an editable window: anchor the finish on completed_at (or
  // the scheduled end), and default the start two hours before it — or to the
  // scheduled start when we have one — so the supervisor drags/types from a real
  // point instead of an empty field.
  const [seedStart, seedFinish] = (() => {
    const finish = firstMs(completedAt, scheduledEnd, scheduledStart) ?? Date.now();
    const startCandidate = firstMs(startedAt, scheduledStart);
    const start =
      startCandidate != null && startCandidate <= finish ? startCandidate : finish - TWO_HOURS_MS;
    return [start, finish];
  })();
  const [startMs, setStartMs] = useState(seedStart);
  const [finishMs, setFinishMs] = useState(seedFinish);
  const [win, setWin] = useState(() => computeWindow(startMs, finishMs));
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<null | 'start' | 'finish'>(null);
  const [, bump] = useReducer((n) => n + 1, 0);

  // Re-fit the axis to the values after a (non-drag) input edit — so a big typed
  // correction rescales the bar — but never mid-drag (that would make it jump).
  useEffect(() => {
    if (!draggingRef.current) setWin(computeWindow(startMs, finishMs));
  }, [startMs, finishMs]);

  const span = win.hi - win.lo || 1;
  const xOf = (t: number) => ((t - win.lo) / span) * 100;

  function startDrag(which: 'start' | 'finish', e: ReactPointerEvent) {
    e.preventDefault();
    const track = trackRef.current;
    if (!track) return;
    draggingRef.current = which;
    bump();
    const frozen = win; // freeze the axis for the duration of this drag
    const other = which === 'start' ? finishMs : startMs;
    const rect = track.getBoundingClientRect();
    const move = (ev: PointerEvent) => {
      const x = Math.min(Math.max(ev.clientX - rect.left, 0), rect.width);
      let t = frozen.lo + (x / rect.width) * (frozen.hi - frozen.lo);
      t = Math.round(t / SNAP_MS) * SNAP_MS;
      if (which === 'start') setStartMs(Math.min(t, other));
      else setFinishMs(Math.max(t, other));
    };
    const up = () => {
      draggingRef.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      bump();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  const ordered = finishMs >= startMs;

  async function save() {
    if (!ordered || update.isPending) return;
    try {
      await update.mutateAsync({
        started_at: new Date(startMs).toISOString(),
        completed_at: new Date(finishMs).toISOString(),
      });
      toast.success('Timeline updated');
      onClose();
    } catch (err) {
      toast.error(`Couldn't update: ${(err as Error).message}`);
    }
  }

  const dragging = draggingRef.current;

  return (
    <div className="rounded-xl border border-line bg-fill-soft p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-ink-faint">
          Adjust timeline
        </span>
        <span className="text-[11px] text-ink-faint">Supervisor override · drag or type</span>
      </div>

      {/* Draggable bar: the working segment between the Start and Finish handles. */}
      <div className="pt-5 pb-1">
        <div
          ref={trackRef}
          className="relative h-2.5 rounded-full bg-fill"
          style={{ touchAction: 'none' }}
        >
          {/* Working segment */}
          <div
            className="absolute top-0 bottom-0 rounded-full bg-accent/30"
            style={{ left: `${xOf(startMs)}%`, right: `${100 - xOf(finishMs)}%` }}
          />
          {(['start', 'finish'] as const).map((which) => {
            const ms = which === 'start' ? startMs : finishMs;
            return (
              <button
                key={which}
                type="button"
                onPointerDown={(e) => startDrag(which, e)}
                aria-label={`${which === 'start' ? 'Start' : 'Finish'} time`}
                className={cnHandle(dragging === which)}
                style={{ left: `${xOf(ms)}%` }}
              >
                <span className="pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium text-ink-dim">
                  {format(new Date(ms), 'h:mma').toLowerCase()}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Precise inputs (gross corrections) + live duration. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs text-ink-dim">Started</span>
          <input
            type="datetime-local"
            value={Number.isNaN(startMs) ? '' : toLocalInput(startMs)}
            onChange={(e) => {
              const ms = localToMs(e.target.value);
              if (!Number.isNaN(ms)) setStartMs(ms);
            }}
            className="mt-1 w-full rounded-lg border border-line bg-bg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-dim">Completed</span>
          <input
            type="datetime-local"
            value={Number.isNaN(finishMs) ? '' : toLocalInput(finishMs)}
            onChange={(e) => {
              const ms = localToMs(e.target.value);
              if (!Number.isNaN(ms)) setFinishMs(ms);
            }}
            className="mt-1 w-full rounded-lg border border-line bg-bg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-sm">
          {ordered ? (
            <>
              <span className="text-ink-dim">Counts</span>{' '}
              <span className="font-semibold tabular-nums">
                {fmtDuration(
                  countedWorkedMinutes(
                    startMs,
                    finishMs,
                    undefined,
                    localScheduledDays(scheduledStart, scheduledEnd),
                  ) * 60_000,
                )}
              </span>
            </>
          ) : (
            <span className="text-status-blocked-fg">Finish must be on or after start.</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void save()} disabled={!ordered || update.isPending}>
            {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Handle styling — a draggable knob centered on its position. */
function cnHandle(active: boolean): string {
  return [
    'absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-accent bg-bg shadow',
    'cursor-grab touch-none focus:outline-none focus:ring-2 focus:ring-accent',
    active && 'cursor-grabbing ring-2 ring-accent',
  ]
    .filter(Boolean)
    .join(' ');
}
